import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { RequireAuth } from '@/lib/auth';
import { LoadingScreen } from '@/components/feedback/LoadingScreen';
import { RouteError } from '@/components/feedback/RouteError';

// Route-level code splitting keeps the first paint small; the shell renders
// immediately and each screen streams in behind a skeleton.
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const ProjectsPage = lazy(() => import('@/features/projects/pages/ProjectsPage'));
const ProjectDetailsPage = lazy(() => import('@/features/projects/pages/ProjectDetailsPage'));
const ActivitiesPage = lazy(() => import('@/features/activities/pages/ActivitiesPage'));
const ActivityDetailsPage = lazy(() => import('@/features/activities/pages/ActivityDetailsPage'));
const ReportProgressPage = lazy(() => import('@/features/updates/pages/ReportProgressPage'));
const AttentionPage = lazy(() => import('@/features/attention/pages/AttentionPage'));
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage'));
const TeamPage = lazy(() => import('@/features/team/pages/TeamPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const SignInPage = lazy(() => import('@/features/auth/pages/SignInPage'));

const withSuspense = (element: React.ReactNode) => (
  <Suspense fallback={<LoadingScreen />}>{element}</Suspense>
);

export const routes: RouteObject[] = [
  // The only route outside the shell: there is no organization to frame yet.
  {
    path: '/signin',
    // No skeleton here: the dashboard placeholder would flash the wrong screen.
    element: (
      <Suspense fallback={null}>
        <SignInPage />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: withSuspense(<DashboardPage />) },
      { path: 'projects', element: withSuspense(<ProjectsPage />) },
      { path: 'projects/:projectId', element: withSuspense(<ProjectDetailsPage />) },
      { path: 'activities', element: withSuspense(<ActivitiesPage />) },
      { path: 'activities/:activityId', element: withSuspense(<ActivityDetailsPage />) },
      {
        path: 'activities/:activityId/reportar',
        element: withSuspense(<ReportProgressPage />),
      },
      { path: 'attention', element: withSuspense(<AttentionPage />) },
      { path: 'reports', element: withSuspense(<ReportsPage />) },
      { path: 'team', element: withSuspense(<TeamPage />) },
      { path: 'settings', element: <Navigate to="/" replace /> },
      { path: '*', element: withSuspense(<NotFoundPage />) },
    ],
  },
];

export const router = createBrowserRouter(routes);
